/**
 * job-canvas.js — Job management for the Greenfield canvas
 *
 * Owns:
 *  - addJobToSite, removeJobFromSite, setJobManagedByInstance, updateJobField
 *  - Job → Server link picker: buildJobServerPickerMarkup, openJobServerPicker,
 *    closeJobServerPicker
 *  - syncProtectionTogglesFromJobs (also window-bridged for greenfield-sortables.js)
 *
 * Extracted from §JOB-CANVAS in script.js (was lines 3200–3440).
 * Dead code removed: buildJobListMarkup (jobs now rendered via recalc.js
 * buildJobRowMarkup / buildExistingEnvGroupMarkup).
 *
 * Mutations emit bus events instead of calling script.js functions directly,
 * breaking the circular dependency that previously locked this code inside script.js.
 */

import { emit, BUS_EVENTS } from './bus.js';
import { sites } from './state.js';
import { scheduleRenderEstimate } from './recalc.js';
import { syncMatrixToHiddenInputs, step2MatrixState, renderScopeMatrix } from './scope-matrix.js';
import {
    getSiteById,
    syncProtectionTileVisuals,
    getCompatibleServersForJob,
    normalizeWorkloadCustomLabel,
} from './helpers.js';
import { getJobTypeDef, sureBackupAllowedWorkloads, getDefaultAgentVersion } from './workload-defs.js';
import { escapeHtml } from './utils.js';

// ── Job management ─────────────────────────────────────────────────────────────

export function addJobToSite(siteId, jobType = 'backup', workloadType = 'vmware', count = 50) {
    const site = getSiteById(siteId);
    if (!site) return;
    if (!Array.isArray(site.jobs)) site.jobs = [];
    site.jobs.push(window.buildDefaultJobEntry(jobType, workloadType, count));
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    syncProtectionTogglesFromJobs();
    emit(BUS_EVENTS.DESIGN_SYNC_NEEDED);
    emit(BUS_EVENTS.COUNTS_SYNC_NEEDED);
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
    scheduleRenderEstimate();
}

export function removeJobFromSite(siteId, jobId) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.jobs)) return;
    site.jobs = site.jobs.filter((j) => j.id !== jobId);
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    syncProtectionTogglesFromJobs();
    emit(BUS_EVENTS.DESIGN_SYNC_NEEDED);
    emit(BUS_EVENTS.COUNTS_SYNC_NEEDED);
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
    scheduleRenderEstimate();
}

/**
 * Set (or clear) the server instance managing a job.
 * Pass instanceId = null to unlink.
 */
export function setJobManagedByInstance(siteId, jobId, instanceId) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.jobs)) return;
    const job = site.jobs.find((j) => j.id === jobId);
    if (!job) return;
    job.managedByInstanceId = instanceId || null;
    emit(BUS_EVENTS.SITE_LIST_CHANGED);
    scheduleRenderEstimate();
}

// ── Job → Server link picker ──────────────────────────────────────────────────

let _jobServerPickerState = null; // { jobId, siteId, anchorEl }

export function buildJobServerPickerMarkup(job, siteId) {
    const compatible = getCompatibleServersForJob(job);
    if (!compatible.length) {
        return `<div class="gf-job-server-picker gf-job-server-picker--empty">
            <p class="gf-job-server-picker-empty">No compatible servers added yet.<br>Add a Veeam server in Step 1 first.</p>
            <button type="button" class="gf-job-server-picker-cancel" data-gf-job-server-picker-close>Close</button>
        </div>`;
    }

    // Group by site
    const bySite = new Map();
    for (const { server, site } of compatible) {
        if (!bySite.has(site.id)) bySite.set(site.id, { site, servers: [] });
        bySite.get(site.id).servers.push(server);
    }

    const isMulti = bySite.size > 1;
    let optionsHtml = '';
    for (const { site, servers } of bySite.values()) {
        if (isMulti) {
            optionsHtml += `<p class="gf-job-server-picker-site-label">${escapeHtml(site.name || `Site ${site.id}`)}</p>`;
        }
        for (const server of servers) {
            const isActive = job.managedByInstanceId === server.instanceId;
            optionsHtml += `<button type="button"
                class="gf-job-server-picker-opt${isActive ? ' is-active' : ''}"
                data-gf-job-server-select="${server.instanceId}"
                data-gf-job-server-select-job="${job.id}"
                data-gf-job-server-select-site="${siteId}">
                <span class="gf-job-server-picker-opt-type">${escapeHtml(server.type.toUpperCase())}</span>
                <span class="gf-job-server-picker-opt-name">${escapeHtml(server.name)}</span>
                ${isActive ? '<span class="gf-job-server-picker-opt-check">✓</span>' : ''}
            </button>`;
        }
    }

    const unlinkHtml = job.managedByInstanceId
        ? `<button type="button" class="gf-job-server-picker-unlink"
                data-gf-job-server-select="null"
                data-gf-job-server-select-job="${job.id}"
                data-gf-job-server-select-site="${siteId}">Unlink</button>`
        : '';

    return `<div class="gf-job-server-picker">
        <p class="gf-job-server-picker-label">Managed by:</p>
        <div class="gf-job-server-picker-opts">${optionsHtml}</div>
        ${unlinkHtml}
        <button type="button" class="gf-job-server-picker-cancel" data-gf-job-server-picker-close>Cancel</button>
    </div>`;
}

export function openJobServerPicker(anchorEl, job, siteId) {
    closeJobServerPicker();

    let portal = document.getElementById('gf-job-server-picker-portal');
    if (!portal) {
        portal = document.createElement('div');
        portal.id = 'gf-job-server-picker-portal';
        document.body.appendChild(portal);
    }

    portal.innerHTML = buildJobServerPickerMarkup(job, siteId);
    portal.hidden = false;

    // Position below the anchor
    const rect = anchorEl.getBoundingClientRect();
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    portal.style.position = 'absolute';
    portal.style.top  = `${rect.bottom + scrollY + 4}px`;
    portal.style.left = `${rect.left  + scrollX}px`;
    portal.style.zIndex = '9999';

    // Clamp to viewport right edge
    requestAnimationFrame(() => {
        const pw = portal.offsetWidth;
        const vw = document.documentElement.clientWidth;
        if (rect.left + pw > vw - 8) {
            portal.style.left = `${Math.max(8, vw - pw - 8) + scrollX}px`;
        }
    });

    _jobServerPickerState = { jobId: job.id, siteId, anchorEl };
}

export function closeJobServerPicker() {
    const portal = document.getElementById('gf-job-server-picker-portal');
    if (portal) { portal.hidden = true; portal.innerHTML = ''; }
    _jobServerPickerState = null;
}

/** Read-only accessor — lets script.js check picker state without a direct reference. */
export function getJobServerPickerState() { return _jobServerPickerState; }

export function updateJobField(siteId, jobId, field, value) {
    const site = getSiteById(siteId);
    if (!site || !Array.isArray(site.jobs)) return;
    const job = site.jobs.find((j) => j.id === jobId);
    if (!job) return;
    if (field === 'jobType') {
        job.jobType = value;
        const typeDef = getJobTypeDef(value);
        if (!typeDef?.hasCount) {
            job.count = 0;
            job.workloadType = '';
        } else if (value === 'surebackup') {
            // Ensure workload type is valid for SureBackup; reset to vmware if not
            if (!job.workloadType || !sureBackupAllowedWorkloads.has(job.workloadType)) {
                job.workloadType = 'vmware';
            }
        } else if (!job.workloadType) {
            job.workloadType = 'vmware';
        }
        // Partially re-render just this site to update UI (count/workload visibility)
        emit(BUS_EVENTS.SITE_LIST_CHANGED);
        syncProtectionTogglesFromJobs();
    } else if (field === 'workloadType') {
        job.workloadType = value;
        // Reset agent version to default whenever the workload type changes
        job.agentVersion = getDefaultAgentVersion(value);
        emit(BUS_EVENTS.SITE_LIST_CHANGED);
    } else if (field === 'count') {
        job.count = Math.max(0, parseInt(value, 10) || 0);
    } else if (field === 'agentVersion') {
        job.agentVersion = value;
        // Version change affects effort calculation but doesn't need a full re-render
        emit(BUS_EVENTS.COUNTS_SYNC_NEEDED);
        scheduleRenderEstimate();
        return;
    } else if (field === 'customLabel') {
        const trimmed = normalizeWorkloadCustomLabel(value);
        if (trimmed) { job.customLabel = trimmed; } else { delete job.customLabel; }
        return; // label change doesn't need full re-calculation
    }
    emit(BUS_EVENTS.COUNTS_SYNC_NEEDED);
    emit(BUS_EVENTS.VALIDATION_SYNC_NEEDED);
    scheduleRenderEstimate();
}

/**
 * Sync step 2 protection toggles from the canvas job list.
 * This keeps the protection tile visuals and sub-zone visibility
 * (replication sub-zone → SureReplica, CDP) in step 2 consistent with
 * what jobs have been added on the canvas.
 */
export function syncProtectionTogglesFromJobs() {
    // Re-render the scope matrix so managed rows reflect updated jobs,
    // then let syncMatrixToHiddenInputs propagate the matrix state to hidden inputs.
    renderScopeMatrix();
    syncMatrixToHiddenInputs();

    // CDP replication: combine managed-job canvas entries + unmanaged matrix selections
    const allJobsForCdp = sites.flatMap((s) => Array.isArray(s.jobs) ? s.jobs : []);
    const hasCdpFromJobsNow   = allJobsForCdp.some((j) => j.jobType === 'cdp-replication');
    const hasCdpFromMatrixNow = Object.values(step2MatrixState.unmanaged).some((s) => s.protection?.cdpReplication);
    const cdpToggle = document.getElementById('cdp-replication-required');
    if (cdpToggle) cdpToggle.checked = hasCdpFromJobsNow || hasCdpFromMatrixNow;

    // Refresh dependent visuals
    window.syncReplicationFields?.();
    syncProtectionTileVisuals();
}
// Window bridge consumed by greenfield-sortables.js
window.syncProtectionTogglesFromJobs = syncProtectionTogglesFromJobs;
